import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import { HeaderLanguageSelector } from '../components/common/HeaderLanguageSelector';
import config from '../config/env';

/**
 * The door.
 *
 * One column, one card, one action. A signed-out visitor was previously shown
 * a navigation rail, a theme switch and a list of features they could not
 * reach — furniture for an application they are not yet inside.
 *
 * The brand is present but restrained: the logo, and the accent on the single
 * button. A full-height gradient panel identified the product twice over and
 * gave the eye somewhere else to go than the one thing to do here.
 *
 * Credentials are never typed on this page. Authentication belongs to
 * Keycloak and the button hands the visitor over to it; saying so is better
 * than a control that navigates somewhere unannounced.
 */
export const SignInPage: React.FC = () => {
  const { login } = useAuth();
  const { t } = useI18n();

  return (
    <div className="signin">
      <div className="signin-topbar">
        <HeaderLanguageSelector />
      </div>

      <main className="signin-card">
        <img
          src={config.ui.logoUrl}
          alt=""
          className="signin-logo"
          onError={e => {
            // Decoration only: a missing file must not leave a broken-image
            // icon on the one screen every single person sees.
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />

        <p className="signin-product">{config.ui.title}</p>
        <h1 className="signin-title">{t('auth.welcome')}</h1>
        <p className="signin-subtitle">{t('auth.signInSubtitle')}</p>

        <button
          type="button"
          className="signin-button"
          data-testid="btn-signin"
          onClick={login}
        >
          {t('auth.signInAction')}
        </button>

        <p className="signin-note">{t('auth.signInRedirectNote')}</p>
      </main>

      <p className="signin-footer">{config.ui.companyName}</p>
    </div>
  );
};
